import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { CopyIcon, EyeIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";

interface Augmentation {
  augmentation_id: string;
  original_request_id: string;
  date_created: number;
  date_completed?: number;
  request_count: number;
  status: string;
}

interface AugmentationsTableProps {
  augmentations: Augmentation[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  datasetId: string;
  emptyTitle?: string;
  emptyDescription?: string;
}

const AugmentationsTable: React.FC<AugmentationsTableProps> = ({
  augmentations,
  loading,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  datasetId,
  emptyTitle = "No augmentations found",
  emptyDescription = "Start a new augmentation to see results here."
}) => {
  const navigate = useNavigate();

  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const getStatusTagColor = (status: string): string => {
    switch (status?.toUpperCase()?.trim()) {
      case "CREATED":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
      case "RUNNING":
      case "PROCESSING":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      case "COMPLETED":
      case "COMPLETE":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "FAILED":
      case "ERROR":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const columns: PaginatedTableColumn<Augmentation>[] = [
    {
      key: "original_request_id",
      header: "Original Request",
      width: "w-[20%]",
      render: (augmentation) => (
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
            {augmentation.original_request_id.length > 12
              ? `${augmentation.original_request_id.substring(0, 6)}...${augmentation.original_request_id.substring(
                  augmentation.original_request_id.length - 6
                )}`
              : augmentation.original_request_id}
          </code>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(augmentation.original_request_id);
            }}
          >
            <CopyIcon className="w-3 h-3" />
          </Button>
        </div>
      )
    },
    {
      key: "date_created",
      header: "Date Started",
      width: "w-[20%]",
      render: (augmentation) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(augmentation.date_created)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(augmentation.date_created)}
          </div>
        </div>
      )
    },
    {
      key: "date_completed",
      header: "Date Completed",
      width: "w-[20%]",
      render: (augmentation) => (
        augmentation.date_completed ? (
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {formatDate(augmentation.date_completed)}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatTime(augmentation.date_completed)}
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )
      )
    },
    {
      key: "request_count",
      header: "# Augmentations",
      width: "w-[15%]",
      render: (augmentation) => (
        <span className="text-sm font-gc-med text-foreground">
          {augmentation.request_count}
        </span>
      )
    },
    {
      key: "status",
      header: "Status",
      width: "w-[15%]",
      render: (augmentation) => (
        <span className={cn(
          "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
          getStatusTagColor(augmentation.status)
        )}>
          {augmentation.status}
        </span>
      )
    },
    {
      key: "augmentation_id",
      header: "Actions",
      width: "w-[10%]",
      render: (augmentation) => (
        <Button
          variant="ghost"
          size="sm"
          className="gap-1"
          disabled={augmentation.status !== "COMPLETED"}
          onClick={() => navigate(`/forge/datasets/${datasetId}/augmentations/${augmentation.augmentation_id}`)}
        >
          <EyeIcon className="w-3 h-3" />
          View
        </Button>
      )
    }
  ];

  return (
    <PaginatedTable
      data={augmentations}
      columns={columns}
      loading={loading}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={itemsPerPage}
      totalItems={totalItems}
      serverSidePagination={true}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      rowClassName="hover:bg-accent/20 transition-colors"
    />
  );
};

export default AugmentationsTable; 